import { JSX, useEffect, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore framer-motion is in the parent dir
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, PencilIcon, TrashIcon, XIcon } from 'lucide-react';
import { Box } from 'lucide-react';

import { ActionButton } from 'components/ActionButton';
import { Checkbox } from 'components/Checkbox';
import { Label } from 'components/Label';
import { Select } from 'components/Select';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import {
  unitsList,
  weightUnitsList,
} from 'pages/QuoteView/LoadBuilding/TurvoSectionForms/constants';
import { NormalizedLoad } from 'types/Load';
import { Maybe, MaybeUndef } from 'types/UtilityTypes';
import { cn } from 'utils/shadcn';

interface CommodityItemCardProps {
  index: number;
  formMethods: UseFormReturn<NormalizedLoad>;
  onRemove: (index: number) => void;
  fieldPath?: 'commodities';
}

const ANIMATIONS = {
  card: {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
  },
  toggle: {
    initial: { opacity: 0, height: 0 },
    animate: { opacity: 1, height: 'auto' },
    exit: { opacity: 0, height: 0 },
    transition: { duration: 0.15 },
  },
  actions: {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.8 },
    transition: { duration: 0.1 },
  },
};

type CommodityFormRow = NormalizedLoad['commodities'][number];

/** Snapshot editable Turvo commodity fields so discard can restore cleared optional state. */
function commodityEditSnapshot(
  item: CommodityFormRow | undefined
): Partial<CommodityFormRow> {
  return {
    ...item,
    value: item?.value,
    perishable: item?.perishable ?? false,
    itemsMinTemp: item?.itemsMinTemp,
    itemsMaxTemp: item?.itemsMaxTemp,
  };
}

function formatItemValueUSD(value: MaybeUndef<number>): string | null {
  if (value == null || Number.isNaN(value) || value <= 0) {
    return null;
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatItemTemperature(
  minTemp: MaybeUndef<number>,
  maxTemp: MaybeUndef<number>
): string | null {
  const hasMin = Number.isFinite(minTemp);
  const hasMax = Number.isFinite(maxTemp);

  if (!hasMin && !hasMax) {
    return null;
  }

  if (hasMin && hasMax) {
    return minTemp === maxTemp ? `${minTemp}°F` : `${minTemp}°F - ${maxTemp}°F`;
  }

  if (hasMin) {
    return `${minTemp}°F min`;
  }

  return `${maxTemp}°F max`;
}

function parseItemTempInput(value: unknown): number | undefined {
  if (value === '' || value == null) {
    return undefined;
  }

  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(n)) {
    return undefined;
  }

  return Math.round(n);
}

export function CommodityItemCard({
  index,
  formMethods,
  onRemove,
  fieldPath = 'commodities',
}: CommodityItemCardProps): JSX.Element {
  const { control, watch } = formMethods;
  const [isEditing, setIsEditing] = useState(false);
  const [originalValues, setOriginalValues] =
    useState<Maybe<Partial<NormalizedLoad['commodities'][0]>>>(null);

  const watchedItem = watch(`${fieldPath}.${index}`);
  const isEmptyItem = !watchedItem?.description;

  const handlingQtyDisplay = Number(watchedItem?.handlingQuantity) || 0;
  const handlingUnitDisplay = (watchedItem?.handlingUnitTypeName || '').trim();
  const hasHandlingData =
    handlingQtyDisplay > 0 || handlingUnitDisplay.length > 0;

  // Start in editing mode if this is an empty item
  useEffect(() => {
    if (isEmptyItem && !isEditing) {
      // Capture initial state so discard works before the user clicks edit.
      setOriginalValues(commodityEditSnapshot(watchedItem));
      setIsEditing(true);
    }
  }, [isEmptyItem, isEditing, watchedItem]);

  // Handlers for the edit, save, discard, and remove item actions
  const handlers = {
    edit: () => {
      setOriginalValues(commodityEditSnapshot(watchedItem));
      setIsEditing(true);
    },
    save: () => {
      setIsEditing(false);
      setOriginalValues(null);
    },
    discard: () => {
      if (originalValues) {
        Object.keys(originalValues).forEach((key) => {
          const fieldName = `${fieldPath}.${index}.${key}` as any;
          formMethods.setValue(
            fieldName,
            originalValues[key as keyof typeof originalValues],
            { shouldValidate: true }
          );
        });
      }
      setIsEditing(false);
      setOriginalValues(null);
    },
    remove: () => onRemove(index),
  };

  // Render the actions for the commodity input card
  const renderActions = () => (
    <AnimatePresence mode='wait'>
      {isEditing ? (
        <motion.div
          key='editing-actions'
          {...ANIMATIONS.actions}
          className='flex gap-1.5'
        >
          {!isEmptyItem ? (
            <>
              <ActionButton
                onClick={handlers.save}
                className='hover:bg-info-200 text-info-600'
                icon={CheckIcon}
                aria-label='Save changes'
              />
              <ActionButton
                onClick={handlers.discard}
                className='hover:bg-error-200 text-error-600'
                icon={XIcon}
                aria-label='Discard changes'
              />
            </>
          ) : (
            <ActionButton
              onClick={handlers.remove}
              className='hover:bg-error-300 text-error-600'
              icon={TrashIcon}
              aria-label='Remove item'
            />
          )}
        </motion.div>
      ) : (
        <motion.div
          key='view-actions'
          {...ANIMATIONS.actions}
          className='flex gap-1.5'
        >
          <ActionButton
            onClick={handlers.edit}
            className='hover:bg-info-300 text-info-600'
            icon={PencilIcon}
            aria-label='Edit item'
          />
          <ActionButton
            onClick={handlers.remove}
            className='hover:bg-error-400 text-error-600'
            icon={TrashIcon}
            aria-label='Remove item'
          />
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Handle Enter key contextually
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      // If hitting enter in the editing form, save changes and collapse form
      if (isEditing) {
        e.preventDefault();
        e.stopPropagation();
        handlers.save();
      } else {
        return;
      }
    }
  };

  // Render the editing form for the commodity input card
  const renderEditingForm = () => (
    <motion.div
      key='editing-form'
      {...ANIMATIONS.toggle}
      className='overflow-hidden'
    >
      {/* Top header with item number and actions */}
      <Flex justify='between' align='center' className='mb-2'>
        <Flex align='center' gap='sm'>
          <Box className='h-4 w-4' />
          <Typography
            variant='caption'
            className='text-neutral-600 font-semibold'
          >
            {index + 1}
          </Typography>
        </Flex>

        {/* Action buttons */}
        <div onClick={(e) => e.stopPropagation()}>{renderActions()}</div>
      </Flex>

      <div className='mx-0 w-full mb-2'>
        <RHFTextInput
          name={`${fieldPath}.${index}.description`}
          label='Item Name'
          required={false}
        />
      </div>

      <Grid cols='2' gap='sm' className='mx-0 w-full'>
        <div className='col-span-1'>
          <RHFTextInput
            name={`${fieldPath}.${index}.totalPieces.val`}
            label='Quantity'
            inputType='number'
            options={{ valueAsNumber: true, min: 0 }}
            required={false}
          />
        </div>

        <div className='col-span-1'>
          <Label
            name={`${fieldPath}.${index}.totalPieces.unit`}
            required={false}
          >
            Units
          </Label>
          <Controller
            name={`${fieldPath}.${index}.totalPieces.unit`}
            control={control}
            render={({ field }) => (
              <Select
                showSearch
                className='h-8'
                placeholder={'Choose'}
                optionFilterProp='children'
                onChange={field.onChange}
                value={field.value}
                options={unitsList?.map((unit) => ({
                  value: unit,
                  label: unit,
                }))}
              />
            )}
          />
        </div>
      </Grid>

      <Grid cols='2' gap='sm' className='mx-0 w-full mt-3'>
        <div className='col-span-1'>
          <RHFTextInput
            name={`${fieldPath}.${index}.handlingQuantity`}
            label='Handling quantity'
            inputType='number'
            options={{ valueAsNumber: true, min: 0 }}
            required={false}
          />
        </div>

        <div className='col-span-1'>
          <Label
            name={`${fieldPath}.${index}.handlingUnitTypeName`}
            required={false}
          >
            Handling units
          </Label>
          <Controller
            name={`${fieldPath}.${index}.handlingUnitTypeName`}
            control={control}
            render={({ field }) => (
              <Select
                showSearch
                className='h-8'
                placeholder={'Choose'}
                optionFilterProp='children'
                onChange={field.onChange}
                value={field.value}
                options={unitsList?.map((unit) => ({
                  value: unit,
                  label: unit,
                }))}
              />
            )}
          />
        </div>
      </Grid>

      <Grid cols='2' gap='sm' className='mx-0 w-full mt-2'>
        <div className='col-span-1'>
          <RHFTextInput
            name={`${fieldPath}.${index}.grossWeight.val`}
            label='Gross Weight'
            inputType='number'
            options={{ valueAsNumber: true, min: 0 }}
            required={false}
          />
        </div>

        <div className='col-span-1'>
          <Label
            name={`${fieldPath}.${index}.grossWeight.unit`}
            required={false}
          >
            Units
          </Label>
          <Controller
            name={`${fieldPath}.${index}.grossWeight.unit`}
            control={control}
            render={({ field }) => (
              <Select
                showSearch
                className='h-8'
                placeholder={'Choose'}
                optionFilterProp='children'
                onChange={field.onChange}
                value={field.value}
                options={weightUnitsList?.map((unit) => ({
                  value: unit,
                  label: unit,
                }))}
              />
            )}
          />
        </div>
      </Grid>

      <Grid cols='2' gap='sm' className='mx-0 w-full mt-2'>
        <div className='col-span-1'>
          <RHFTextInput
            name={`${fieldPath}.${index}.netWeight.val`}
            label='Net Weight'
            inputType='number'
            options={{ valueAsNumber: true, min: 0 }}
            required={false}
          />
        </div>

        <div className='col-span-1'>
          <Label name={`${fieldPath}.${index}.netWeight.unit`} required={false}>
            Units
          </Label>
          <Controller
            name={`${fieldPath}.${index}.netWeight.unit`}
            control={control}
            render={({ field }) => (
              <Select
                showSearch
                className='h-8'
                placeholder={'Choose'}
                optionFilterProp='children'
                onChange={field.onChange}
                value={field.value}
                options={weightUnitsList?.map((unit) => ({
                  value: unit,
                  label: unit,
                }))}
              />
            )}
          />
        </div>
      </Grid>

      <div className='mx-0 w-full mt-3'>
        <RHFTextInput
          name={`${fieldPath}.${index}.value`}
          label='Item value (USD)'
          inputType='number'
          options={{
            min: 0,
            setValueAs: (v) => {
              if (v === '' || v == null) return undefined;
              const n = typeof v === 'number' ? v : Number(v);
              return Number.isNaN(n) ? undefined : n;
            },
          }}
          required={false}
          inputClassName='no-spin-buttons'
        />
      </div>

      <div className='mx-0 w-full mt-2 pb-1'>
        <Controller
          name={`${fieldPath}.${index}.perishable`}
          control={control}
          render={({ field }) => (
            <Checkbox
              label='Perishable'
              labelClassName='leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-neutral-500'
              checked={field.value ?? false}
              onCheckedChange={(checked) => field.onChange(checked)}
            />
          )}
        />
      </div>

      <Grid cols='2' gap='sm' className='mx-0 w-full mt-2 pb-1'>
        <div className='col-span-1'>
          <RHFTextInput
            name={`${fieldPath}.${index}.itemsMinTemp`}
            label='Min temp (°F)'
            inputType='number'
            step={1}
            options={{
              setValueAs: parseItemTempInput,
            }}
            required={false}
            inputClassName='no-spin-buttons'
          />
        </div>

        <div className='col-span-1'>
          <RHFTextInput
            name={`${fieldPath}.${index}.itemsMaxTemp`}
            label='Max temp (°F)'
            inputType='number'
            step={1}
            options={{
              setValueAs: parseItemTempInput,
            }}
            required={false}
            inputClassName='no-spin-buttons'
          />
        </div>
      </Grid>
    </motion.div>
  );

  // Render the view mode/collapsed mode for the commodity input card
  const renderViewMode = () => {
    if (!watchedItem?.description) return null;

    const formattedItemValue = formatItemValueUSD(watchedItem.value);
    const formattedItemTemperature = formatItemTemperature(
      watchedItem.itemsMinTemp,
      watchedItem.itemsMaxTemp
    );

    return (
      <motion.div
        key='view-mode'
        {...ANIMATIONS.toggle}
        className='overflow-hidden cursor-pointer'
        onClick={handlers.edit}
        onKeyDown={(e: React.KeyboardEvent) =>
          e.key === 'Enter' && handlers.edit()
        }
        role='button'
        tabIndex={0}
        aria-label='Click to edit item'
      >
        <Flex justify='between' align='center' className='min-w-0'>
          <Flex align='center' gap='sm' className='flex-1 min-w-0 mr-2'>
            <Box className='h-4 w-4' />
            <Typography
              variant='caption'
              className='text-neutral-600 font-medium truncate'
              title={watchedItem.description}
              aria-label={watchedItem.description}
            >
              {watchedItem.description}
            </Typography>
          </Flex>

          {/* Action buttons */}
          <Flex onClick={(e) => e.stopPropagation()} className='flex-shrink-0'>
            {renderActions()}
          </Flex>
        </Flex>

        <Flex align='center' gap='sm' className='text-sm text-neutral-900'>
          <Typography variant='body-xs' className='text-neutral-500'>
            Quantity:
          </Typography>
          <Typography variant='body-xs' className='text-neutral-600'>
            {watchedItem.totalPieces?.val || 0}
          </Typography>
          {watchedItem.totalPieces?.unit && (
            <>
              <Typography variant='body-xs' className='text-neutral-500'>
                •
              </Typography>
              <Typography variant='body-xs' className='text-neutral-500'>
                {watchedItem.totalPieces.unit}
              </Typography>
            </>
          )}
          {hasHandlingData && (
            <>
              <Typography variant='body-xs' className='text-neutral-500'>
                •
              </Typography>
              <Typography variant='body-xs' className='text-neutral-500'>
                Handling:
              </Typography>
              <Typography variant='body-xs' className='text-neutral-600'>
                {handlingQtyDisplay}
              </Typography>
              {handlingUnitDisplay ? (
                <>
                  <Typography variant='body-xs' className='text-neutral-500'>
                    •
                  </Typography>
                  <Typography variant='body-xs' className='text-neutral-500'>
                    {handlingUnitDisplay}
                  </Typography>
                </>
              ) : null}
            </>
          )}
          {(watchedItem.grossWeight?.val || watchedItem.netWeight?.val) && (
            <>
              <Typography variant='body-xs' className='text-neutral-500'>
                •
              </Typography>
              <Typography variant='body-xs' className='text-neutral-500'>
                Weight:{' '}
                {watchedItem.grossWeight?.val || watchedItem.netWeight?.val}{' '}
                {watchedItem.grossWeight?.unit ||
                  watchedItem.netWeight?.unit ||
                  'lbs'}
              </Typography>
            </>
          )}
          {formattedItemValue && (
            <>
              <Typography variant='body-xs' className='text-neutral-500'>
                •
              </Typography>
              <Typography variant='body-xs' className='text-neutral-500'>
                Value: {formattedItemValue}
              </Typography>
            </>
          )}
          {watchedItem.perishable && (
            <>
              <Typography variant='body-xs' className='text-neutral-500'>
                •
              </Typography>
              <Typography variant='body-xs' className='text-neutral-500'>
                Perishable
              </Typography>
            </>
          )}
          {formattedItemTemperature && (
            <>
              <Typography variant='body-xs' className='text-neutral-500'>
                •
              </Typography>
              <Typography variant='body-xs' className='text-neutral-500'>
                Temp: {formattedItemTemperature}
              </Typography>
            </>
          )}
        </Flex>
      </motion.div>
    );
  };

  return (
    <motion.div
      layout
      transition={{ type: 'spring', duration: 0.2, bounce: 0 }}
      {...ANIMATIONS.card}
      className={cn(
        'mb-2 last:mb-0 overflow-hidden rounded-[4px] border bg-linear-to-br shadow-sm relative transition-all duration-200 p-2.5',
        isEmptyItem
          ? 'border-neutral-400 bg-linear-to-br from-neutral-50 to-neutral-100/50'
          : 'border-neutral-500 bg-linear-to-br from-neutral-50 to-info-50/50'
      )}
      onKeyDown={handleKeyDown}
    >
      <AnimatePresence mode='wait'>
        {isEditing ? renderEditingForm() : renderViewMode()}
      </AnimatePresence>
    </motion.div>
  );
}
